import { useEffect, useMemo, useState, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, ArrowsClockwise, Play, X } from "@phosphor-icons/react";
import api from "../lib/api";
import { useCountry } from "../context/CountryContext";
import RouteReplayModal from "../components/RouteReplayModal";
import "leaflet/dist/leaflet.css";

// Build a rotated arrow marker as a divIcon. Colour reflects state:
//   green   = moving (speed ≥ 3 km/h)
//   amber   = engine on but stationary (idle-running risk)
//   red     = moving AND low fuel
//   grey    = parked (engine off) OR offline
function buildArrowIcon({ heading = 0, speed = 0, engine, online, lowFuel, selected, plate }) {
  const moving = (speed || 0) >= 3;
  // Colour semantics — updated 2026-07-17 per ops feedback ("show red for stopped/offline"):
  //   grey    = truck is moving normally (green was too easy to miss on the map background)
  //   red     = stopped OR offline OR engine off (needs attention)
  //   amber   = engine on but not moving (idle-running risk — likely fuel waste)
  //   orange  = moving AND low fuel (< 25%)
  let fill = "#dc2626"; // red default — "not moving" is now the loud state
  if (!online) fill = "#dc2626";
  else if (moving && lowFuel) fill = "#f97316";  // orange = fuel warning while running
  else if (moving) fill = "#10b981";              // green = healthy movement
  else if (engine === "on") fill = "#f59e0b";     // amber = idle while running
  else fill = "#dc2626";                          // red = engine off / stopped
  const stroke = selected ? "#0284c7" : "#ffffff";
  const strokeW = selected ? 3 : 1.5;
  const size = selected ? 34 : 26;
  // If we don't have a heading (parked / not enough history) draw a circle instead
  const shape = moving && heading !== null && heading !== undefined
    ? `<polygon points="12,2 20,22 12,17 4,22" fill="${fill}" stroke="${stroke}" stroke-width="${strokeW}" stroke-linejoin="round"/>`
    : `<circle cx="12" cy="12" r="8" fill="${fill}" stroke="${stroke}" stroke-width="${strokeW}"/>`;
  const rot = moving ? `transform:rotate(${heading}deg);` : "";
  // Plate label — always visible above the marker so ops can scan the map without hovering.
  const plateLabel = plate
    ? `<div style="position:absolute;bottom:${size + 2}px;left:50%;transform:translateX(-50%);background:rgba(255,255,255,.95);color:#0b1f3a;font-size:10px;font-weight:800;padding:1px 5px;border-radius:4px;white-space:nowrap;font-family:ui-monospace,monospace;box-shadow:0 1px 2px rgba(0,0,0,.2);border:1px solid rgba(0,0,0,.06);letter-spacing:.02em">${plate}</div>`
    : "";
  const speedBadge = moving && speed
    ? `<div style="position:absolute;top:${size + 2}px;left:50%;transform:translateX(-50%);background:${fill};color:#fff;font-size:10px;font-weight:700;padding:1px 5px;border-radius:8px;white-space:nowrap;font-family:ui-monospace,monospace;box-shadow:0 1px 2px rgba(0,0,0,.3)">${Math.round(speed)} km/h</div>`
    : "";
  return L.divIcon({
    className: "fleet-arrow-marker",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `<div style="position:relative;width:${size}px;height:${size}px;">${plateLabel}<svg viewBox="0 0 24 24" style="width:100%;height:100%;${rot}transform-origin:center;filter:drop-shadow(0 1px 2px rgba(0,0,0,.35))">${shape}</svg>${speedBadge}</div>`,
  });
}

// Auto-fit bounds when the position set changes AND center on a specific plate if requested
function AutoFocus({ positions, focusPlate }) {
  const map = useMap();
  useEffect(() => {
    if (!positions.length) return;
    if (focusPlate) {
      const target = positions.find((p) => p.vehicle_number === focusPlate);
      if (target) {
        map.setView([target.lat, target.lng], 14);
        return;
      }
    }
    const latlngs = positions.map((p) => [p.lat, p.lng]);
    if (latlngs.length === 1) map.setView(latlngs[0], 11);
    else map.fitBounds(latlngs, { padding: [40, 40], maxZoom: 12 });
  }, [positions, focusPlate, map]);
  return null;
}

export default function FleetMapPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { filterParam } = useCountry();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlate, setSelectedPlate] = useState(searchParams.get("vehicle") || null);
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [lowFuelOnly, setLowFuelOnly] = useState(false);
  const [showReplay, setShowReplay] = useState(null); // vehicle_number or null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gps/positions");
      setPositions(Array.isArray(data) ? data : (data.positions || []));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Auto-refresh every 60s
  useEffect(() => {
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const filtered = useMemo(() => {
    return positions
      .filter((p) => typeof p.lat === "number" && typeof p.lng === "number")
      .filter((p) => !filterParam || p.country === filterParam)
      .filter((p) => !onlineOnly || p.online)
      .filter((p) => !lowFuelOnly || (typeof p.fuel_level_pct === "number" && p.fuel_level_pct < 25));
  }, [positions, filterParam, onlineOnly, lowFuelOnly]);

  const selectedPos = useMemo(
    () => positions.find((p) => p.vehicle_number === selectedPlate) || null,
    [positions, selectedPlate]
  );

  const selectPlate = (plate) => {
    setSelectedPlate(plate);
    const next = new URLSearchParams(searchParams);
    if (plate) next.set("vehicle", plate); else next.delete("vehicle");
    setSearchParams(next, { replace: true });
  };

  const stats = useMemo(() => {
    const online = filtered.filter((p) => p.online).length;
    const lowFuel = filtered.filter((p) => typeof p.fuel_level_pct === "number" && p.fuel_level_pct < 25).length;
    return { total: filtered.length, online, lowFuel };
  }, [filtered]);

  return (
    <div className="flex flex-col md:flex-row h-[calc(100vh-56px)] md:h-screen bg-slate-50" data-testid="fleet-map-page">
      {/* MAP fills remaining space */}
      <div className="flex-1 min-w-0 relative">
        {/* Top bar overlay */}
        <div className="absolute top-3 left-3 right-3 z-[500] flex flex-wrap items-center gap-2 pointer-events-none">
          <div className="pointer-events-auto bg-white/95 backdrop-blur border border-slate-200 rounded-md px-3 py-2 shadow-sm flex items-center gap-3">
            <MapPin size={16} weight="duotone" className="text-ps-blue" />
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-ps-deep">Fleet Map</div>
            <div className="text-[11px] text-slate-500" data-testid="fleet-map-stats">
              <span className="font-mono-data font-bold text-ps-deep">{stats.total}</span> total ·
              <span className="text-emerald-600 font-mono-data font-bold"> {stats.online}</span> online ·
              <span className="text-red-600 font-mono-data font-bold"> {(stats.total - stats.online)}</span> stopped ·
              <span className="text-orange-500 font-mono-data font-bold"> {stats.lowFuel}</span> low-fuel
            </div>
          </div>
          <div className="pointer-events-auto flex gap-1.5">
            <label className={`bg-white/95 backdrop-blur border rounded-md px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider cursor-pointer shadow-sm ${onlineOnly ? "border-emerald-500 text-emerald-700" : "border-slate-200 text-slate-600"}`}>
              <input type="checkbox" checked={onlineOnly} onChange={(e) => setOnlineOnly(e.target.checked)} data-testid="map-filter-online" className="mr-1 accent-emerald-500" />
              Online only
            </label>
            <label className={`bg-white/95 backdrop-blur border rounded-md px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider cursor-pointer shadow-sm ${lowFuelOnly ? "border-red-500 text-red-700" : "border-slate-200 text-slate-600"}`}>
              <input type="checkbox" checked={lowFuelOnly} onChange={(e) => setLowFuelOnly(e.target.checked)} data-testid="map-filter-low-fuel" className="mr-1 accent-red-500" />
              Low fuel
            </label>
            <button onClick={load} data-testid="map-refresh" title="Refresh" className="bg-white/95 backdrop-blur border border-slate-200 rounded-md p-1.5 text-slate-600 hover:text-ps-blue shadow-sm">
              <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        <MapContainer
          center={[5.6, -0.2]}   // Accra / Tema default centre
          zoom={9}
          className="w-full h-full"
          scrollWheelZoom
          attributionControl
          data-testid="fleet-map"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <AutoFocus positions={filtered} focusPlate={selectedPlate} />
          {filtered.map((p) => {
            const isSelected = p.vehicle_number === selectedPlate;
            const lowFuel = typeof p.fuel_level_pct === "number" && p.fuel_level_pct < 25;
            const icon = buildArrowIcon({
              heading: p.heading,
              speed: p.speed_kmh,
              engine: p.engine,
              online: p.online,
              lowFuel,
              selected: isSelected,
              plate: p.vehicle_number,
            });
            return (
              <Marker
                key={`${p.source || "gps"}-${p.vehicle_number}`}
                position={[p.lat, p.lng]}
                icon={icon}
                eventHandlers={{ click: () => selectPlate(p.vehicle_number) }}
              >
                <Popup>
                  <div className="text-xs">
                    <div className="font-bold text-sm">{p.vehicle_number}</div>
                    <div className="text-slate-600 mt-0.5">{p.address || "—"}</div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      {typeof p.speed_kmh === "number" && <>Speed: <b>{p.speed_kmh} km/h</b> </>}
                      {typeof p.heading === "number" && <>· heading {p.heading}°</>}
                    </div>
                    <button
                      onClick={() => selectPlate(p.vehicle_number)}
                      className="mt-2 text-[10px] uppercase tracking-wider font-bold text-ps-blue underline"
                    >
                      Open details →
                    </button>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      {/* Right side panel — only when a truck is selected */}
      {selectedPos && (
        <aside
          className="w-full md:w-[360px] shrink-0 bg-white border-l border-slate-200 flex flex-col overflow-y-auto"
          data-testid="map-detail-panel"
        >
          <div className="px-4 py-3 border-b border-slate-200 flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-ps-blue font-semibold">Truck</div>
              <h2 className="font-display font-extrabold text-2xl text-ps-deep tracking-tight" data-testid="map-detail-plate">
                {selectedPos.vehicle_number}
              </h2>
              {selectedPos.license_plate && selectedPos.license_plate !== selectedPos.vehicle_number && (
                <div className="text-[10px] text-slate-500 font-mono-data mt-0.5">plate: {selectedPos.license_plate}</div>
              )}
            </div>
            <button onClick={() => selectPlate(null)} data-testid="map-detail-close" className="p-1 rounded hover:bg-slate-100">
              <X size={18} weight="bold" />
            </button>
          </div>

          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Cell label="Status" value={selectedPos.online ? "Online" : "Offline"} className={selectedPos.online ? "text-emerald-600" : "text-slate-500"} />
              <Cell label="Country" value={selectedPos.country || "?"} />
              <Cell label="Speed" value={typeof selectedPos.speed_kmh === "number" ? `${selectedPos.speed_kmh} km/h` : "—"} />
              <Cell label="Engine" value={selectedPos.engine || "—"} />
              <Cell
                label="Fuel"
                value={typeof selectedPos.fuel_level_pct === "number" ? `${selectedPos.fuel_level_pct}%` : "—"}
                className={typeof selectedPos.fuel_level_pct === "number" && selectedPos.fuel_level_pct < 25 ? "text-red-600" : ""}
              />
              <Cell label="Last seen" value={selectedPos.last_seen ? String(selectedPos.last_seen).slice(5, 16).replace("T", " ") : "—"} />
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold mb-1">Address</div>
              <div className="text-sm text-slate-800">{selectedPos.address || "—"}</div>
              <a
                href={`https://www.google.com/maps?q=${selectedPos.lat},${selectedPos.lng}`}
                target="_blank" rel="noreferrer"
                data-testid="map-detail-gmaps"
                className="mt-1 text-[11px] text-ps-blue hover:underline inline-flex items-center gap-1"
              >
                Open in Google Maps →
              </a>
            </div>

            <div className="border-t border-slate-200 pt-3">
              <button
                onClick={() => setShowReplay(selectedPos.vehicle_number)}
                data-testid="map-detail-replay"
                className="w-full inline-flex items-center justify-center gap-2 bg-ps-blue text-white px-3 py-2.5 text-xs uppercase tracking-[0.18em] font-bold rounded hover:bg-ps-deep"
              >
                <Play size={14} weight="fill" />
                Route replay — last 24 h
              </button>
              <Link
                to={`/activities?vehicle=${encodeURIComponent(selectedPos.vehicle_number)}`}
                data-testid="map-detail-activities"
                className="mt-2 w-full inline-flex items-center justify-center gap-2 border border-slate-300 text-slate-700 px-3 py-2 text-[11px] uppercase tracking-wider font-semibold rounded hover:bg-slate-50"
              >
                Recent trips →
              </Link>
            </div>
          </div>
        </aside>
      )}

      {showReplay && (
        <RouteReplayModal vehicleNumber={showReplay} onClose={() => setShowReplay(null)} />
      )}
    </div>
  );
}

function Cell({ label, value, className = "" }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
      <div className={`font-mono-data text-sm font-bold text-ps-deep ${className}`}>{value}</div>
    </div>
  );
}
